import type { NotificationUnreadCount, Project } from "@facade/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { notificationsApi } from "@facade/services";
import { notificationQueryKeys } from "../queries";
import { projectQueryKeys } from "@facade/features/projects/queries";
import {
  clearProjectUnreadNotificationCounts,
  updateNotificationLists,
} from "./notificationCache";

export function useMarkAllNotificationsReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsApi.markAllRead,
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: notificationQueryKeys.all }),
        queryClient.cancelQueries({
          queryKey: projectQueryKeys.all,
          exact: true,
        }),
      ]);
      const previousLists = queryClient.getQueriesData({
        queryKey: notificationQueryKeys.lists(),
      });
      const previousCount = queryClient.getQueryData<NotificationUnreadCount>(
        notificationQueryKeys.unreadCount(),
      );
      const previousProjects = queryClient.getQueryData<Project[]>(
        projectQueryKeys.all,
      );
      const readAt = new Date().toISOString();

      updateNotificationLists(queryClient, (notification) =>
        notification.readAt ? notification : { ...notification, readAt },
      );
      queryClient.setQueryData<NotificationUnreadCount>(
        notificationQueryKeys.unreadCount(),
        { count: 0 },
      );
      clearProjectUnreadNotificationCounts(queryClient);

      return { previousLists, previousCount, previousProjects };
    },
    onError: (_error, _variables, context) => {
      context?.previousLists.forEach(([queryKey, value]) =>
        queryClient.setQueryData(queryKey, value),
      );
      if (context?.previousCount) {
        queryClient.setQueryData(
          notificationQueryKeys.unreadCount(),
          context.previousCount,
        );
      }
      if (context?.previousProjects) {
        queryClient.setQueryData(
          projectQueryKeys.all,
          context.previousProjects,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
    },
  });
}
